import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { COUNTRIES, getProvincesForCountry } from '../../config/contractorConstants';

/**
 * Format phone number as (xxx)-xxx-xxxx
 * Handles input progressively as user types
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Strip all non-digits
  const digits = value.replace(/\D/g, '');
  // Limit to 10 digits (standard North American)
  const limited = digits.slice(0, 10);
  // Format progressively as user types
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 3)})-${limited.slice(3)}`;
  return `(${limited.slice(0, 3)})-${limited.slice(3, 6)}-${limited.slice(6)}`;
};

/**
 * Get file type icon based on mime type or extension
 */
const getFileIcon = (fileType, filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (fileType?.includes('pdf') || ext === 'pdf') return { icon: '📄', color: '#dc3545' };
  if (fileType?.includes('word') || ext === 'doc' || ext === 'docx') return { icon: '📝', color: '#2b579a' };
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet') || ext === 'xls' || ext === 'xlsx') return { icon: '📊', color: '#217346' };
  return { icon: '📎', color: '#6c757d' };
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Format date/time for comment display
 */
const formatCommentDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Comment Card Component - Expandable card for displaying a single comment
 */
const CommentCard = memo(({ comment, isExpanded, onToggle }) => {
  const displayName = comment.user_name || comment.user_email || 'Unknown User';
  const isLongComment = comment.comment && comment.comment.length > 100;
  
  return (
    <div 
      style={{
        backgroundColor: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        marginBottom: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
      }}
    >
      {/* Card Banner - Always Visible */}
      <div 
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          backgroundColor: '#f8f9fa',
          cursor: isLongComment ? 'pointer' : 'default',
          borderBottom: isExpanded ? '1px solid #e9ecef' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ 
            fontWeight: '600', 
            color: '#495057',
            fontSize: '0.9rem'
          }}>
            {displayName}
          </span>
          <span style={{ 
            color: '#6c757d', 
            fontSize: '0.8rem',
            backgroundColor: '#e9ecef',
            padding: '0.2rem 0.5rem',
            borderRadius: '4px'
          }}>
            {formatCommentDate(comment.created_at)}
          </span>
        </div>
        {isLongComment && (
          <span style={{ 
            color: '#6c757d', 
            fontSize: '0.85rem',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        )}
      </div>
      
      {/* Comment Content */}
      <div style={{
        padding: '0.75rem 1rem',
        fontSize: '0.9rem',
        color: '#333',
        lineHeight: '1.5',
        maxHeight: isExpanded || !isLongComment ? 'none' : '3.5em',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {comment.comment}
        {!isExpanded && isLongComment && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1.5em',
            background: 'linear-gradient(transparent, white)'
          }} />
        )}
      </div>
    </div>
  );
});

/**
 * Evaluation Card Component - Expandable card for displaying a single evaluation
 */
const EvaluationCard = memo(({ evaluation, isExpanded, onToggle }) => {
  const displayName = evaluation.user_name || evaluation.user_email || 'Unknown User';
  
  return (
    <div 
      style={{
        backgroundColor: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        marginBottom: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
      }}
    >
      {/* Card Banner - Always Visible */}
      <div 
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          backgroundColor: '#e3f2fd',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #e9ecef' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ 
            fontWeight: '600', 
            color: '#495057',
            fontSize: '0.9rem'
          }}>
            {displayName}
          </span>
          <span style={{ 
            color: '#6c757d', 
            fontSize: '0.8rem',
            backgroundColor: '#bbdefb',
            padding: '0.2rem 0.5rem',
            borderRadius: '4px'
          }}>
            {formatCommentDate(evaluation.created_at)}
          </span>
        </div>
        <span style={{ 
          color: '#6c757d', 
          fontSize: '0.85rem',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      </div>
      
      {/* Evaluation Content - Always show summary, expand for details */}
      <div style={{
        padding: '0.75rem 1rem',
        fontSize: '0.9rem',
        color: '#333'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          {/* Insurance - Yes/No */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>🛡️ Ins:</span>
            <span style={{
              backgroundColor: evaluation.insurance ? '#d4edda' : '#f8d7da',
              color: evaluation.insurance ? '#155724' : '#721c24',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.insurance ? 'Yes' : 'No'}
            </span>
          </div>
          
          {/* Quality - 1-5 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>⭐ Qual:</span>
            <span style={{
              backgroundColor: evaluation.quality >= 4 ? '#d4edda' : evaluation.quality >= 3 ? '#fff3cd' : '#f8d7da',
              color: evaluation.quality >= 4 ? '#155724' : evaluation.quality >= 3 ? '#856404' : '#721c24',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.quality ?? '-'}
            </span>
          </div>
          
          {/* HSE - 1-5 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>🦺 HSE:</span>
            <span style={{
              backgroundColor: evaluation.hse >= 4 ? '#d4edda' : evaluation.hse >= 3 ? '#fff3cd' : '#f8d7da',
              color: evaluation.hse >= 4 ? '#155724' : evaluation.hse >= 3 ? '#856404' : '#721c24',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.hse ?? '-'}
            </span>
          </div>
          
          {/* Orientation - Yes/No */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>🧭 Orient:</span>
            <span style={{
              backgroundColor: evaluation.orientation ? '#d4edda' : '#f8d7da',
              color: evaluation.orientation ? '#155724' : '#721c24',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.orientation ? 'Yes' : 'No'}
            </span>
          </div>
          
          {/* Performance - 1-5 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>📈 Perf:</span>
            <span style={{
              backgroundColor: evaluation.performance >= 4 ? '#d4edda' : evaluation.performance >= 3 ? '#fff3cd' : '#f8d7da',
              color: evaluation.performance >= 4 ? '#155724' : evaluation.performance >= 3 ? '#856404' : '#721c24',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.performance ?? '-'}
            </span>
          </div>
          
          {/* Commercial - 1-5 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }} title="Commercial Documentation">💼 Comm:</span>
            <span style={{
              backgroundColor: evaluation.commercial >= 4 ? '#d4edda' : evaluation.commercial >= 3 ? '#fff3cd' : evaluation.commercial ? '#f8d7da' : '#e9ecef',
              color: evaluation.commercial >= 4 ? '#155724' : evaluation.commercial >= 3 ? '#856404' : evaluation.commercial ? '#721c24' : '#666',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.commercial ?? '-'}
            </span>
          </div>
          
          {/* Technical - 1-5 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ color: '#666', fontSize: '0.8rem' }} title="Technical Documentation">📐 Tech:</span>
            <span style={{
              backgroundColor: evaluation.technical >= 4 ? '#d4edda' : evaluation.technical >= 3 ? '#fff3cd' : evaluation.technical ? '#f8d7da' : '#e9ecef',
              color: evaluation.technical >= 4 ? '#155724' : evaluation.technical >= 3 ? '#856404' : evaluation.technical ? '#721c24' : '#666',
              padding: '0.15rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {evaluation.technical ?? '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Evaluations Section Component - Collapsible history with add new evaluation form
 */
const EvaluationsSection = memo(({ 
  contractorId, 
  instance, 
  accounts,
  onEvaluationSaved,
  isEditMode = true
}) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false); // Collapsed by default
  const [expandedEvalIds, setExpandedEvalIds] = useState(new Set());
  
  // New evaluation form state
  const [newEval, setNewEval] = useState({
    insurance: false,
    quality: '',
    hse: '',
    orientation: false,
    performance: '',
    commercial: '',
    technical: ''
  });
  
  // Fetch evaluations when contractor changes
  useEffect(() => {
    if (contractorId) {
      fetchEvaluations();
    }
  }, [contractorId]);
  
  const fetchEvaluations = async () => {
    if (!contractorId) return;
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractorId}/evaluations`);
      if (response.data.success) {
        setEvaluations(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching evaluations:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveEvaluation = async () => {
    // Validate that at least one field has a value
    const hasValue = newEval.insurance || newEval.quality || newEval.hse || newEval.orientation || newEval.performance || newEval.commercial || newEval.technical;
    if (!hasValue) {
      alert('Please fill in at least one evaluation field');
      return;
    }
    
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractorId}/evaluations`, {
        insurance: newEval.insurance,
        quality: newEval.quality || null,
        hse: newEval.hse || null,
        orientation: newEval.orientation,
        performance: newEval.performance || null,
        commercial: newEval.commercial || null,
        technical: newEval.technical || null
      });
      
      if (response.data.success) {
        // Prepend new evaluation to the list
        setEvaluations([response.data.data, ...evaluations]);
        // Reset form
        setNewEval({
          insurance: false,
          quality: '',
          hse: '',
          orientation: false,
          performance: '',
          commercial: '',
          technical: ''
        });
        // Notify parent to refresh contractors list
        if (onEvaluationSaved) {
          onEvaluationSaved();
        }
      }
    } catch (err) {
      console.error('Error saving evaluation:', err);
      alert(err.response?.data?.message || 'Failed to save evaluation');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleEvalExpanded = useCallback((evalId) => {
    setExpandedEvalIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(evalId)) {
        newSet.delete(evalId);
      } else {
        newSet.add(evalId);
      }
      return newSet;
    });
  }, []);
  
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Evaluation History Section */}
      <div style={{ 
        marginBottom: '1rem',
        border: '1px solid #90caf9',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Collapsible Header */}
        <div 
          onClick={() => setHistoryExpanded(!historyExpanded)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            backgroundColor: '#bbdefb',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <h4 style={{ 
            margin: 0, 
            color: '#1565c0', 
            fontSize: '1rem', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            ⭐ Evaluation History ({evaluations.length})
          </h4>
          <span style={{ 
            fontSize: '0.9rem',
            color: '#1565c0',
            transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>
        
        {/* Scrollable Evaluations Container */}
        {historyExpanded && (
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '1rem',
            backgroundColor: '#e3f2fd'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                Loading evaluations...
              </div>
            ) : evaluations.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem 1rem', 
                color: '#999',
                fontSize: '0.9rem'
              }}>
                No evaluations yet. Add the first evaluation below!
              </div>
            ) : (
              evaluations.map(evaluation => (
                <EvaluationCard
                  key={evaluation.id}
                  evaluation={evaluation}
                  isExpanded={expandedEvalIds.has(evaluation.id)}
                  onToggle={() => toggleEvalExpanded(evaluation.id)}
                />
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Add New Evaluation Form - Only visible in edit mode */}
      {isEditMode && (
      <div style={{ 
        padding: '1rem',
        backgroundColor: '#e8f5e9',
        borderRadius: '8px',
        border: '1px solid #a5d6a7'
      }}>
        <label style={{ 
          display: 'block', 
          fontSize: '0.9rem', 
          marginBottom: '0.75rem', 
          fontWeight: 'bold', 
          color: '#2e7d32' 
        }}>
          Add New Evaluation
        </label>
        
        {/* First row - Score fields (5 columns) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {/* Quality - Dropdown 1-5 */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }}>
              ⭐ Quality
            </label>
            <select
              value={newEval.quality}
              onChange={(e) => setNewEval({ ...newEval, quality: e.target.value })}
              style={{
                width: '100%',
                padding: '0.4rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}
            >
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          
          {/* HSE - Dropdown 1-5 */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }}>
              🦺 HSE
            </label>
            <select
              value={newEval.hse}
              onChange={(e) => setNewEval({ ...newEval, hse: e.target.value })}
              style={{
                width: '100%',
                padding: '0.4rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}
            >
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          
          {/* Performance - Dropdown 1-5 */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }}>
              📈 Perf
            </label>
            <select
              value={newEval.performance}
              onChange={(e) => setNewEval({ ...newEval, performance: e.target.value })}
              style={{
                width: '100%',
                padding: '0.4rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}
            >
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          
          {/* Commercial - Dropdown 1-5 */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }} title="Commercial Documentation - contracts, pricing, terms, proposals">
              💼 Comm
            </label>
            <select
              value={newEval.commercial}
              onChange={(e) => setNewEval({ ...newEval, commercial: e.target.value })}
              style={{
                width: '100%',
                padding: '0.4rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}
              title="Commercial Documentation - contracts, pricing, terms, proposals"
            >
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          
          {/* Technical - Dropdown 1-5 */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }} title="Technical Documentation - specifications, drawings, certifications, procedures">
              📐 Tech
            </label>
            <select
              value={newEval.technical}
              onChange={(e) => setNewEval({ ...newEval, technical: e.target.value })}
              style={{
                width: '100%',
                padding: '0.4rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                textAlign: 'center'
              }}
              title="Technical Documentation - specifications, drawings, certifications, procedures"
            >
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
        </div>
        
        {/* Second row - Boolean fields (2 columns) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
          {/* Insurance - Checkbox */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }}>
              🛡️ Insurance
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.35rem',
              cursor: 'pointer',
              padding: '0.4rem',
              backgroundColor: newEval.insurance ? '#c8e6c9' : '#ffcdd2',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}>
              <input
                type="checkbox"
                checked={newEval.insurance}
                onChange={(e) => setNewEval({ ...newEval, insurance: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                {newEval.insurance ? 'Yes' : 'No'}
              </span>
            </label>
          </div>
          
          {/* Orientation - Checkbox */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem', color: '#666' }}>
              🧭 Orientation
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.35rem',
              cursor: 'pointer',
              padding: '0.4rem',
              backgroundColor: newEval.orientation ? '#c8e6c9' : '#ffcdd2',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}>
              <input
                type="checkbox"
                checked={newEval.orientation}
                onChange={(e) => setNewEval({ ...newEval, orientation: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                {newEval.orientation ? 'Yes' : 'No'}
              </span>
            </label>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSaveEvaluation}
            disabled={saving}
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: saving ? '#6c757d' : '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : '⭐ Save Evaluation'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
});

/**
 * Win/Loss Section Component - Track proposal wins and losses
 */
const WinLossSection = memo(({ 
  contractorId, 
  instance, 
  accounts,
  onWinLossChange,
  isEditMode = true
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ wins: 0, losses: 0, total: 0 });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({ result_type: '', project_name: '', notes: '' });
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const fetchRecords = useCallback(async () => {
    if (!contractorId) return;
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractorId}/win-loss`);
      if (response.data.success) {
        setRecords(response.data.data || []);
        setSummary(response.data.summary || { wins: 0, losses: 0, total: 0 });
      }
    } catch (err) {
      console.error('Error fetching win/loss records:', err);
    } finally {
      setLoading(false);
    }
  }, [contractorId, instance, accounts]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleAddRecord = async (resultType) => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractorId}/win-loss`, {
        result_type: resultType,
        project_name: newRecord.project_name || null,
        notes: newRecord.notes || null
      });
      if (response.data.success) {
        setRecords([response.data.data, ...records]);
        setSummary(prev => ({
          ...prev,
          wins: resultType === 'win' ? prev.wins + 1 : prev.wins,
          losses: resultType === 'loss' ? prev.losses + 1 : prev.losses,
          total: prev.total + 1
        }));
        setNewRecord({ result_type: '', project_name: '', notes: '' });
        setShowAddForm(false);
        if (onWinLossChange) onWinLossChange();
      }
    } catch (err) {
      console.error('Error adding win/loss record:', err);
      alert(err.response?.data?.message || 'Failed to add record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRecord = async (recordId, resultType) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/contractors/${contractorId}/win-loss/${recordId}`);
      if (response.data.success) {
        setRecords(records.filter(r => r.id !== recordId));
        setSummary(prev => ({
          ...prev,
          wins: resultType === 'win' ? prev.wins - 1 : prev.wins,
          losses: resultType === 'loss' ? prev.losses - 1 : prev.losses,
          total: prev.total - 1
        }));
        if (onWinLossChange) onWinLossChange();
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      alert('Failed to delete record');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={{ 
      marginBottom: '1.5rem', 
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
      overflow: 'hidden'
    }}>
      {/* Header with Summary */}
      <div style={{ 
        padding: '1rem 1.25rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h4 style={{ margin: 0, fontSize: '1rem', color: '#495057' }}>🏆 Win/Loss Record</h4>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <span style={{ 
              backgroundColor: '#d4edda', 
              color: '#155724', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 'bold'
            }}>
              ✓ {summary.wins} Wins
            </span>
            <span style={{ 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 'bold'
            }}>
              ✗ {summary.losses} Losses
            </span>
          </div>
        </div>
        {isEditMode && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '0.4rem 0.75rem',
              backgroundColor: showAddForm ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add Record'}
          </button>
        )}
      </div>

      {/* Add Form */}
      {isEditMode && showAddForm && (
        <div style={{ 
          padding: '1rem 1.25rem',
          backgroundColor: '#f0f7ff',
          borderBottom: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>
              Project/Proposal Name (optional)
            </label>
            <input
              type="text"
              value={newRecord.project_name}
              onChange={(e) => setNewRecord({ ...newRecord, project_name: e.target.value })}
              placeholder="e.g., ABC Pipeline Project"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>
              Notes (optional)
            </label>
            <textarea
              value={newRecord.notes}
              onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
              placeholder="Any additional details..."
              rows={2}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => handleAddRecord('win')}
              disabled={saving}
              style={{
                flex: 1,
                padding: '0.6rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                opacity: saving ? 0.7 : 1
              }}
            >
              ✓ Record Win
            </button>
            <button
              onClick={() => handleAddRecord('loss')}
              disabled={saving}
              style={{
                flex: 1,
                padding: '0.6rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                opacity: saving ? 0.7 : 1
              }}
            >
              ✗ Record Loss
            </button>
          </div>
        </div>
      )}

      {/* History Toggle */}
      {records.length > 0 && (
        <div 
          onClick={() => setHistoryExpanded(!historyExpanded)}
          style={{ 
            padding: '0.75rem 1.25rem',
            backgroundColor: '#fff',
            borderBottom: historyExpanded ? '1px solid #e9ecef' : 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: '#6c757d',
            fontSize: '0.85rem'
          }}
        >
          <span>📜 History ({records.length} record{records.length !== 1 ? 's' : ''})</span>
          <span style={{ fontSize: '0.9rem' }}>{historyExpanded ? '▼' : '▶'}</span>
        </div>
      )}

      {/* History List */}
      {historyExpanded && records.length > 0 && (
        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
          {records.map((record, idx) => (
            <div 
              key={record.id}
              style={{ 
                padding: '0.75rem 1.25rem',
                borderBottom: idx < records.length - 1 ? '1px solid #f0f0f0' : 'none',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
              }}
            >
              <span style={{ 
                fontSize: '1.25rem',
                marginTop: '2px'
              }}>
                {record.result_type === 'win' ? '🏆' : '❌'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: record.result_type === 'win' ? '#155724' : '#721c24',
                    fontSize: '0.9rem'
                  }}>
                    {record.result_type === 'win' ? 'WIN' : 'LOSS'}
                  </span>
                  {record.project_name && (
                    <span style={{ color: '#495057', fontSize: '0.9rem' }}>
                      — {record.project_name}
                    </span>
                  )}
                </div>
                {record.notes && (
                  <div style={{ color: '#6c757d', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {record.notes}
                  </div>
                )}
                <div style={{ color: '#adb5bd', fontSize: '0.75rem' }}>
                  {formatDate(record.recorded_at)} by {record.recorded_by_name || record.recorded_by_email}
                </div>
              </div>
              {isEditMode && (
                <button
                  onClick={() => handleDeleteRecord(record.id, record.result_type)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'transparent',
                    color: '#dc3545',
                    border: '1px solid #dc3545',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    opacity: 0.7
                  }}
                  title="Delete record"
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
          Loading...
        </div>
      )}
    </div>
  );
});

/**
 * Comments Section Component - Collapsible history with add new comment
 */
const CommentsSection = memo(({ 
  contractorId, 
  instance, 
  accounts,
  isEditMode = true
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false); // Collapsed by default
  const [expandedCommentIds, setExpandedCommentIds] = useState(new Set());
  
  // Fetch comments when contractor changes
  useEffect(() => {
    if (contractorId) {
      fetchComments();
    }
  }, [contractorId]);
  
  const fetchComments = async () => {
    if (!contractorId) return;
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractorId}/comments`);
      if (response.data.success) {
        setComments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractorId}/comments`, {
        comment: newComment.trim()
      });
      
      if (response.data.success) {
        // Prepend new comment to the list
        setComments([response.data.data, ...comments]);
        setNewComment('');
      }
    } catch (err) {
      console.error('Error saving comment:', err);
      alert(err.response?.data?.message || 'Failed to save comment');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleCommentExpanded = useCallback((commentId) => {
    setExpandedCommentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);
  
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Comments History Section */}
      <div style={{ 
        marginBottom: '1rem',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Collapsible Header */}
        <div 
          onClick={() => setHistoryExpanded(!historyExpanded)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            backgroundColor: '#e9ecef',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <h4 style={{ 
            margin: 0, 
            color: '#495057', 
            fontSize: '1rem', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            💬 Comments History ({comments.length})
          </h4>
          <span style={{ 
            fontSize: '0.9rem',
            color: '#6c757d',
            transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>
        
        {/* Scrollable Comments Container */}
        {historyExpanded && (
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '1rem',
            backgroundColor: '#f8f9fa'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                Loading comments...
              </div>
            ) : comments.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem 1rem', 
                color: '#999',
                fontSize: '0.9rem'
              }}>
                No comments yet. Be the first to add a comment!
              </div>
            ) : (
              comments.map(comment => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  isExpanded={expandedCommentIds.has(comment.id)}
                  onToggle={() => toggleCommentExpanded(comment.id)}
                />
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Add New Comment Section - Only visible in edit mode */}
      {isEditMode && (
        <div style={{ 
          padding: '1rem',
          backgroundColor: '#f0f7ff',
          borderRadius: '8px',
          border: '1px solid #b8daff'
        }}>
          <label style={{ 
            display: 'block', 
            fontSize: '0.9rem', 
            marginBottom: '0.5rem', 
            fontWeight: 'bold', 
            color: '#495057' 
          }}>
            Add Comment
          </label>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={3}
            placeholder="Enter your comment here..."
            style={{ 
              width: '100%', 
              padding: '0.75rem', 
              border: '1px solid #ddd', 
              borderRadius: '6px',
              boxSizing: 'border-box',
              fontSize: '0.95rem',
              resize: 'vertical',
              marginBottom: '0.75rem'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleSaveComment}
              disabled={saving || !newComment.trim()}
              style={{
                padding: '0.6rem 1.25rem',
                backgroundColor: saving || !newComment.trim() ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: saving || !newComment.trim() ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: saving || !newComment.trim() ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : '💬 Save Comment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Contractor Detail Modal - View and edit contractor details including contacts, attachments, and comments
 */
const ContractorDetailModal = memo(({ 
  isOpen, 
  onClose, 
  contractor, 
  onSave, 
  saving,
  instance,
  accounts,
  onEvaluationSaved,
  canEdit = true // Permission to edit - if false, user only sees read-only view
}) => {
  const [form, setForm] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [disciplines, setDisciplines] = useState([]); // Fetched from API
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [newContact, setNewContact] = useState({ contact_name: '', phone: '', email: '', province_state: '' });
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactForm, setEditContactForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Tab state - 'details', 'attachments', or 'insurance'
  const [activeTab, setActiveTab] = useState('details');
  
  // Insurance expiry state
  const [insuranceData, setInsuranceData] = useState({
    insurance_expiry_canada: null,
    insurance_expiry_us: null,
    canada_status: 'not_set',
    us_status: 'not_set',
    overall_status: 'not_set'
  });
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [savingInsurance, setSavingInsurance] = useState(false);
  
  // Edit mode state - false = read-only view (default), true = edit view
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Get available provinces based on selected country
  const availableProvinces = getProvincesForCountry(form.country);
  
  // Fetch disciplines from API on mount
  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/contractors/disciplines');
        if (response.data.success) {
          setDisciplines(response.data.data.map(d => d.name));
        }
      } catch (err) {
        console.error('Error fetching disciplines:', err);
      }
    };
    if (isOpen) {
      fetchDisciplines();
    }
  }, [isOpen, instance, accounts]);
  
  // Initialize form and fetch contacts/attachments/insurance when contractor changes
  useEffect(() => {
    if (contractor) {
      setForm({ ...contractor });
      setProvinces(contractor.provinces || []);
      fetchContacts();
      fetchAttachments();
      fetchInsurance();
      // Reset to read-only mode when opening a new contractor
      setIsEditMode(false);
    }
  }, [contractor]);
  
  const fetchContacts = async () => {
    if (!contractor?.id) return;
    try {
      setLoadingContacts(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractor.id}/contacts`);
      if (response.data.success) {
        setContacts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };
  
  const fetchAttachments = async () => {
    if (!contractor?.id) return;
    try {
      setLoadingAttachments(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractor.id}/attachments`);
      if (response.data.success) {
        setAttachments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
    } finally {
      setLoadingAttachments(false);
    }
  };
  
  const fetchInsurance = async () => {
    if (!contractor?.id) return;
    try {
      setLoadingInsurance(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractor.id}/insurance`);
      if (response.data.success) {
        setInsuranceData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching insurance data:', err);
    } finally {
      setLoadingInsurance(false);
    }
  };
  
  const handleSaveInsurance = async () => {
    try {
      setSavingInsurance(true);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/contractors/${contractor.id}/insurance`, {
        insurance_expiry_canada: insuranceData.insurance_expiry_canada || null,
        insurance_expiry_us: insuranceData.insurance_expiry_us || null
      });
      if (response.data.success) {
        setInsuranceData(response.data.data);
        if (onEvaluationSaved) onEvaluationSaved(); // Refresh parent list
      }
    } catch (err) {
      console.error('Error saving insurance data:', err);
      alert(err.response?.data?.message || 'Failed to save insurance data');
    } finally {
      setSavingInsurance(false);
    }
  };
  
  const handleAddContact = async () => {
    if (!newContact.contact_name && !newContact.phone && !newContact.email) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractor.id}/contacts`, newContact);
      if (response.data.success) {
        setContacts([...contacts, response.data.data]);
        setNewContact({ contact_name: '', phone: '', email: '', province_state: '' });
        setShowAddContact(false);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
    }
  };
  
  const handleUpdateContact = async (contactId) => {
    try {
      // If contactId is 'legacy' or null, this is a legacy contact - create new instead of update
      if (contactId === 'legacy' || contactId === null) {
        const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractor.id}/contacts`, editContactForm);
        if (response.data.success) {
          // Replace the legacy contact with the new one
          setContacts(contacts.map(c => c.id === null ? response.data.data : c));
          setEditingContactId(null);
          setEditContactForm({});
        }
      } else {
        const response = await authenticatedApiCall(instance, accounts, 'PUT', `/contractors/${contractor.id}/contacts/${contactId}`, editContactForm);
        if (response.data.success) {
          setContacts(contacts.map(c => c.id === contactId ? response.data.data : c));
          setEditingContactId(null);
          setEditContactForm({});
        }
      }
    } catch (err) {
      console.error('Error updating contact:', err);
    }
  };
  
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      // If contactId is null, this is a legacy contact - just remove from UI
      // The legacy data in contractors_db will be cleaned up when a new contact is added
      if (contactId === null) {
        setContacts(contacts.filter(c => c.id !== null));
        return;
      }
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/contractors/${contractor.id}/contacts/${contactId}`);
      if (response.data.success) {
        setContacts(contacts.filter(c => c.id !== contactId));
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploading(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/contractors/${contractor.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setAttachments([response.data.data, ...attachments]);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      alert(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/${contractor.id}/attachments/${attachment.id}/download`, null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };
  
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/contractors/${contractor.id}/attachments/${attachmentId}`);
      if (response.data.success) {
        setAttachments(attachments.filter(a => a.id !== attachmentId));
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
    }
  };
  
  if (!isOpen || !contractor) return null;
  
  const handleSave = () => {
    // Include provinces in the save data
    onSave({ ...form, provinces });
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1rem',
          borderBottom: '2px solid #007bff',
          paddingBottom: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🏢 Contractor Details
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0',
          marginBottom: '1.5rem',
          borderBottom: '2px solid #dee2e6'
        }}>
          <button
            onClick={() => setActiveTab('details')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: activeTab === 'details' ? '#007bff' : 'transparent',
              color: activeTab === 'details' ? 'white' : '#495057',
              border: 'none',
              borderBottom: activeTab === 'details' ? '3px solid #0056b3' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeTab === 'details' ? '600' : '400',
              transition: 'all 0.2s ease',
              borderRadius: '6px 6px 0 0'
            }}
          >
            📋 Details
          </button>
          <button
            onClick={() => setActiveTab('attachments')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: activeTab === 'attachments' ? '#007bff' : 'transparent',
              color: activeTab === 'attachments' ? 'white' : '#495057',
              border: 'none',
              borderBottom: activeTab === 'attachments' ? '3px solid #0056b3' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeTab === 'attachments' ? '600' : '400',
              transition: 'all 0.2s ease',
              borderRadius: '6px 6px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📎 Attachments
            {attachments.length > 0 && (
              <span style={{
                backgroundColor: activeTab === 'attachments' ? 'white' : '#fd7e14',
                color: activeTab === 'attachments' ? '#007bff' : 'white',
                padding: '0.15rem 0.5rem',
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                {attachments.length}
              </span>
            )}
          </button>
          {isEditMode && (
            <button
              onClick={() => setActiveTab('insurance')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: activeTab === 'insurance' 
                  ? '#007bff' 
                  : insuranceData.overall_status === 'expired' 
                    ? '#dc3545' 
                    : insuranceData.overall_status === 'valid' 
                      ? '#28a745' 
                      : 'transparent',
                color: activeTab === 'insurance' || insuranceData.overall_status !== 'not_set' ? 'white' : '#495057',
                border: 'none',
                borderBottom: activeTab === 'insurance' ? '3px solid #0056b3' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: activeTab === 'insurance' ? '600' : '400',
                transition: 'all 0.2s ease',
                borderRadius: '6px 6px 0 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              title={
                insuranceData.overall_status === 'expired' 
                  ? 'Insurance expired!' 
                  : insuranceData.overall_status === 'valid' 
                    ? 'Insurance valid' 
                    : 'No insurance dates set'
              }
            >
              🛡️ Insurance
              {insuranceData.overall_status === 'expired' && (
                <span style={{ fontSize: '0.8rem' }}>⚠️</span>
              )}
              {insuranceData.overall_status === 'valid' && (
                <span style={{ fontSize: '0.8rem' }}>✓</span>
              )}
            </button>
          )}
        </div>
        
        {/* Details Tab Content */}
        {activeTab === 'details' && (
          <>
        {/* Basic Info Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#495057', fontSize: '1rem', fontWeight: 'bold' }}>
              📋 Basic Information
            </h4>
            {/* Edit button only shows if user has edit permission AND not already in edit mode */}
            {canEdit && !isEditMode && (
              <button
                onClick={() => setIsEditMode(true)}
                title="Edit contractor details"
                style={{
                  padding: '0.4rem 0.6rem',
                  backgroundColor: 'transparent',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: '#007bff',
                  fontSize: '0.85rem'
                }}
              >
                <span style={{ fontSize: '1rem' }}>📁</span> Edit
              </button>
            )}
          </div>
          
          {/* Read-Only View */}
          {!isEditMode ? (
            <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Country</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>{form.country || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Discipline</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>{form.discipline || '-'}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Name</span>
                  <div style={{ fontSize: '1.1rem', color: '#333', marginTop: '0.25rem', fontWeight: '600' }}>{form.contractor || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Type</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>
                    {form.entity_type === 'supplier' ? 'Supplier / Manufacturer' : 'Subcontractor'}
                  </div>
                </div>
              </div>
              
              {/* Province/State as bullet list */}
              {provinces.length > 0 && (
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Province/State</span>
                  <ul style={{ 
                    margin: '0.5rem 0 0 0', 
                    paddingLeft: '1.25rem',
                    listStyleType: 'disc'
                  }}>
                    {provinces.map(p => (
                      <li key={p} style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.25rem' }}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Last used (most recent engagement) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Last Bid #</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>{form.last_bid || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Last Project</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>{form.last_project || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>Last Year</span>
                  <div style={{ fontSize: '0.95rem', color: '#333', marginTop: '0.25rem' }}>{form.last_year || '-'}</div>
                </div>
              </div>
            </div>
          ) : (
          /* Edit View */
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Country *
              </label>
              <select
                value={form.country || ''}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  setForm({ ...form, country: newCountry });
                  // Clear provinces when country changes
                  if (newCountry !== form.country) {
                    setProvinces([]);
                  }
                }}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              >
                <option value="">Select Country</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Discipline *
              </label>
              <select
                value={form.discipline || ''}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              >
                <option value="">Select Discipline</option>
                {disciplines.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Name *
              </label>
              <input
                type="text"
                value={form.contractor || ''}
                onChange={(e) => setForm({ ...form, contractor: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Type *
              </label>
              <select
                value={form.entity_type === 'supplier' ? 'supplier' : 'subcontractor'}
                onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                title="Reclassify this record between subcontractor and supplier"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              >
                <option value="subcontractor">Subcontractor (installs in field)</option>
                <option value="supplier">Supplier / Manufacturer (materials)</option>
              </select>
            </div>
          </div>
          
          {/* Province/State Multi-Select */}
          {form.country && availableProvinces.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold', color: '#666' }}>
                Province/State (select all that apply)
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                gap: '0.5rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {availableProvinces.map(province => (
                  <label 
                    key={province} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: provinces.includes(province) ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={provinces.includes(province)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setProvinces([...provinces, province]);
                        } else {
                          setProvinces(provinces.filter(p => p !== province));
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    {province}
                  </label>
                ))}
              </div>
              {provinces.length > 0 && (
                <div style={{ 
                  marginTop: '0.75rem', 
                  padding: '0.75rem',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '6px',
                  border: '1px solid #90caf9'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#1565c0', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    📍 Selected ({provinces.length}):
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#333' }}>
                    {provinces.join(', ')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Last used (most recent engagement) - editable */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Last Bid #
              </label>
              <input
                type="text"
                value={form.last_bid || ''}
                onChange={(e) => setForm({ ...form, last_bid: e.target.value })}
                placeholder="e.g. EST-2024-005"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Last Project
              </label>
              <input
                type="text"
                value={form.last_project || ''}
                onChange={(e) => setForm({ ...form, last_project: e.target.value })}
                placeholder="Most recent project"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: 'bold', color: '#666' }}>
                Last Year
              </label>
              <input
                type="number"
                value={form.last_year || ''}
                onChange={(e) => setForm({ ...form, last_year: e.target.value })}
                placeholder="e.g. 2024"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          </>
          )}
        </div>
        
        {/* Contacts Section - Multiple Contacts */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#495057', fontSize: '1rem', fontWeight: 'bold' }}>
              📞 Contacts ({contacts.length})
            </h4>
            {isEditMode && (
              <button
                onClick={() => setShowAddContact(true)}
                style={{
                  padding: '0.35rem 0.75rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                + Add Contact
              </button>
            )}
          </div>
          
          {loadingContacts ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Loading contacts...</div>
          ) : contacts.length === 0 && !showAddContact ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#999', fontSize: '0.9rem' }}>
              {isEditMode ? 'No contacts added yet. Click "Add Contact" to add one.' : 'No contacts added yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {contacts.map((contact, idx) => (
                <div key={contact.id ?? `legacy-${idx}`} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem',
                  backgroundColor: contact.isLegacy ? '#fff8e1' : 'white',
                  borderRadius: '6px',
                  border: contact.isLegacy ? '1px solid #ffecb3' : '1px solid #e9ecef',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {isEditMode && editingContactId === (contact.id ?? 'legacy') ? (
                    <>
                      <input
                        type="text"
                        value={editContactForm.contact_name || ''}
                        onChange={(e) => setEditContactForm({ ...editContactForm, contact_name: e.target.value })}
                        placeholder="Name"
                        style={{ flex: '1 1 120px', padding: '0.35rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                      <input
                        type="text"
                        value={editContactForm.phone || ''}
                        onChange={(e) => setEditContactForm({ ...editContactForm, phone: formatPhoneNumber(e.target.value) })}
                        placeholder="Phone"
                        style={{ flex: '1 1 120px', padding: '0.35rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                      <input
                        type="email"
                        value={editContactForm.email || ''}
                        onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })}
                        placeholder="Email"
                        style={{ flex: '1.5 1 160px', padding: '0.35rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                      {availableProvinces.length > 0 && (
                        <select
                          value={editContactForm.province_state || ''}
                          onChange={(e) => setEditContactForm({ ...editContactForm, province_state: e.target.value })}
                          style={{ flex: '1 1 120px', padding: '0.35rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                        >
                          <option value="">Province/State</option>
                          {availableProvinces.map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      )}
                      <button onClick={() => handleUpdateContact(contact.id ?? 'legacy')} style={{ padding: '0.35rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✓</button>
                      <button onClick={() => { setEditingContactId(null); setEditContactForm({}); }} style={{ padding: '0.35rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✕</button>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: '1 1 100px', fontSize: '0.9rem' }}>
                        <strong>{contact.contact_name || '-'}</strong>
                      </div>
                      <div style={{ flex: '1 1 100px', fontSize: '0.85rem', color: '#666' }}>
                        {contact.phone ? formatPhoneNumber(contact.phone) : '-'}
                      </div>
                      <div style={{ flex: '1.5 1 140px', fontSize: '0.85rem', color: '#666' }}>
                        {contact.email || '-'}
                      </div>
                      {contact.province_state && (
                        <div style={{ fontSize: '0.8rem', color: '#0d6efd', backgroundColor: '#e7f1ff', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                          {contact.province_state}
                        </div>
                      )}
                      {isEditMode && (
                        <>
                          <button onClick={() => { setEditingContactId(contact.id ?? 'legacy'); setEditContactForm(contact); }} style={{ padding: '0.35rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>✏️</button>
                          <button onClick={() => handleDeleteContact(contact.id)} style={{ padding: '0.35rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>🗑️</button>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Add Contact Form */}
          {isEditMode && showAddContact && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={newContact.contact_name}
                  onChange={(e) => setNewContact({ ...newContact, contact_name: e.target.value })}
                  placeholder="Contact Name"
                  style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="Phone"
                  style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="Email"
                  style={{ flex: '1.5 1 160px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                />
                {availableProvinces.length > 0 && (
                  <select
                    value={newContact.province_state}
                    onChange={(e) => setNewContact({ ...newContact, province_state: e.target.value })}
                    style={{ flex: '1 1 120px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="">Province/State</option>
                    {availableProvinces.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                )}
                <button onClick={handleAddContact} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add</button>
                <button onClick={() => { setShowAddContact(false); setNewContact({ contact_name: '', phone: '', email: '', province_state: '' }); }} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        
        {/* Evaluations Section - History + Add New Form */}
        <EvaluationsSection
          contractorId={contractor?.id}
          instance={instance}
          accounts={accounts}
          onEvaluationSaved={onEvaluationSaved}
          isEditMode={isEditMode}
        />
        
        {/* Win/Loss Section */}
        <WinLossSection
          contractorId={contractor?.id}
          instance={instance}
          accounts={accounts}
          onWinLossChange={onEvaluationSaved}
          isEditMode={isEditMode}
        />
        
        {/* Comments Section */}
        <CommentsSection
          contractorId={contractor?.id}
          instance={instance}
          accounts={accounts}
          isEditMode={isEditMode}
        />
          </>
        )}
        
        {/* Attachments Tab Content */}
        {activeTab === 'attachments' && (
          <div style={{ padding: '1rem', backgroundColor: '#fff8e1', borderRadius: '8px', minHeight: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, color: '#495057', fontSize: '1.1rem', fontWeight: 'bold' }}>
                📎 Attachments ({attachments.length})
              </h4>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: uploading ? '#6c757d' : '#fd7e14',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  {uploading ? 'Uploading...' : '+ Upload File'}
                </button>
              </div>
            </div>
            <div style={{ 
              fontSize: '0.85rem', 
              color: '#856404', 
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#fffde7',
              borderRadius: '6px',
              border: '1px solid #fff59d'
            }}>
              📋 Allowed file types: PDF, Word (.doc, .docx), Excel (.xls, .xlsx) - Maximum 10MB per file
            </div>
            
            {loadingAttachments ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                Loading attachments...
              </div>
            ) : attachments.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem', 
                color: '#999', 
                fontSize: '0.95rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '2px dashed #ddd'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>No attachments yet</div>
                <div>Click "Upload File" to add documents like insurance certificates, contracts, etc.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {attachments.map(attachment => {
                  const fileInfo = getFileIcon(attachment.file_type, attachment.original_name);
                  return (
                    <div key={attachment.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '1rem', color: fileInfo.color }}>{fileInfo.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '0.95rem', 
                          color: '#007bff', 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: '500'
                        }} onClick={() => handleDownloadAttachment(attachment)}>
                          {attachment.original_name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                          {formatFileSize(attachment.file_size)} • Uploaded by {attachment.uploaded_by}
                        </div>
                      </div>
                      <button 
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        style={{ 
                          padding: '0.5rem 0.75rem', 
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Insurance Tab Content - Only visible in edit mode */}
        {isEditMode && activeTab === 'insurance' && (
          <div style={{ padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: '8px', minHeight: '300px' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#495057', fontSize: '1.1rem', fontWeight: 'bold' }}>
                🛡️ Insurance Expiry Date
              </h4>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '0.85rem' }}>
                Set insurance expiry date for {form.country === 'Canada' ? 'Canadian' : 'U.S.'} coverage. The tab will show green if valid, red if expired.
              </p>
            </div>
            
            {loadingInsurance ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                Loading insurance data...
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Show Canada Row only if country is Canada */}
                {form.country === 'Canada' && (
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1.25rem', 
                    borderRadius: '8px',
                    border: `2px solid ${
                      insuranceData.canada_status === 'expired' ? '#dc3545' :
                      insuranceData.canada_status === 'valid' ? '#28a745' : '#dee2e6'
                    }`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>🇨🇦</span>
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#495057' }}>Canada</span>
                      </div>
                      {insuranceData.canada_status !== 'not_set' && (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          backgroundColor: insuranceData.canada_status === 'expired' ? '#f8d7da' : '#d4edda',
                          color: insuranceData.canada_status === 'expired' ? '#721c24' : '#155724'
                        }}>
                          {insuranceData.canada_status === 'expired' ? '⚠️ EXPIRED' : '✓ VALID'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <label style={{ fontSize: '0.9rem', color: '#666', minWidth: '100px' }}>Expiry Date:</label>
                      <input
                        type="date"
                        value={insuranceData.insurance_expiry_canada ? insuranceData.insurance_expiry_canada.split('T')[0] : ''}
                        onChange={(e) => setInsuranceData({ ...insuranceData, insurance_expiry_canada: e.target.value || null })}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '0.95rem',
                          minWidth: '180px'
                        }}
                      />
                      {insuranceData.insurance_expiry_canada && (
                        <button
                          onClick={() => setInsuranceData({ ...insuranceData, insurance_expiry_canada: null })}
                          style={{
                            padding: '0.4rem 0.6rem',
                            backgroundColor: 'transparent',
                            color: '#dc3545',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                          title="Clear date"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show U.S. Row only if country is U.S. */}
                {form.country === 'U.S.' && (
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '1.25rem', 
                    borderRadius: '8px',
                    border: `2px solid ${
                      insuranceData.us_status === 'expired' ? '#dc3545' :
                      insuranceData.us_status === 'valid' ? '#28a745' : '#dee2e6'
                    }`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>🇺🇸</span>
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#495057' }}>United States</span>
                      </div>
                      {insuranceData.us_status !== 'not_set' && (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          backgroundColor: insuranceData.us_status === 'expired' ? '#f8d7da' : '#d4edda',
                          color: insuranceData.us_status === 'expired' ? '#721c24' : '#155724'
                        }}>
                          {insuranceData.us_status === 'expired' ? '⚠️ EXPIRED' : '✓ VALID'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <label style={{ fontSize: '0.9rem', color: '#666', minWidth: '100px' }}>Expiry Date:</label>
                      <input
                        type="date"
                        value={insuranceData.insurance_expiry_us ? insuranceData.insurance_expiry_us.split('T')[0] : ''}
                        onChange={(e) => setInsuranceData({ ...insuranceData, insurance_expiry_us: e.target.value || null })}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '0.95rem',
                          minWidth: '180px'
                        }}
                      />
                      {insuranceData.insurance_expiry_us && (
                        <button
                          onClick={() => setInsuranceData({ ...insuranceData, insurance_expiry_us: null })}
                          style={{
                            padding: '0.4rem 0.6rem',
                            backgroundColor: 'transparent',
                            color: '#dc3545',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                          title="Clear date"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    onClick={handleSaveInsurance}
                    disabled={savingInsurance}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: savingInsurance ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      opacity: savingInsurance ? 0.7 : 1
                    }}
                  >
                    {savingInsurance ? 'Saving...' : '💾 Save Insurance Date'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          justifyContent: 'flex-end',
          borderTop: '1px solid #eee',
          paddingTop: '1rem'
        }}>
          {!isEditMode ? (
            /* Read-only mode: just Close button */
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem'
              }}
            >
              Close
            </button>
          ) : (
            /* Edit mode: Cancel (exit edit) and Save buttons */
            <>
              <button
                onClick={() => {
                  setIsEditMode(false);
                  // Reset form to original contractor data
                  if (contractor) {
                    setForm({ ...contractor });
                    setProvinces(contractor.provinces || []);
                  }
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default ContractorDetailModal;
